import React, { useMemo } from 'react';
import { Feature } from '../../types';
import { TableCard, TableHeader, TableViewport, SortableTh, EmptyRow, thClass, tdClass, SortOrder, compareRowValues } from '../table/TableKit';
import { urlEnum, useUrlState } from '../../hooks/useUrlState';

const URL_SPEC = {
  sort: urlEnum(['feature_key', 'is_active'] as const, 'feature_key'),
  order: urlEnum(['asc', 'desc'] as const, 'asc'),
};

interface Props {
  features: Feature[];
  changes: Map<string, boolean>;
  onFeatureChange: (featureKey: string, isActive: boolean) => void;
}

const FeaturesTab: React.FC<Props> = ({ features, changes, onFeatureChange }) => {
  const [urlState, setUrlState] = useUrlState(URL_SPEC, 'features');
  const onSort = (sort: string, order: SortOrder) => setUrlState({
    sort: sort as typeof urlState.sort,
    order,
  });
  const sortedFeatures = useMemo(() => {
    // Sort Active by the effective (pending-toggle) value so order matches the checkboxes.
    const activeValue = (f: Feature) => (changes.has(f.feature_key) ? changes.get(f.feature_key)! : f.is_active);
    return [...features].sort((a, b) => {
      const aVal = urlState.sort === 'feature_key' ? a.feature_key : activeValue(a);
      const bVal = urlState.sort === 'feature_key' ? b.feature_key : activeValue(b);
      const comparison = compareRowValues(aVal, bVal);
      return urlState.order === 'desc' ? -comparison : comparison;
    });
  }, [features, urlState.sort, urlState.order, changes]);

  const getEffectiveValue = (feature: Feature): boolean => {
    return changes.has(feature.feature_key) ? changes.get(feature.feature_key)! : feature.is_active;
  };

  const hasChange = (feature: Feature): boolean => {
    return changes.has(feature.feature_key);
  };

  return (
    <TableCard>
      <TableHeader title="Features">
        <p className="text-sm text-secondary">
          Manage global feature flags. They apply to all instances and jobs.
        </p>
      </TableHeader>

      <TableViewport>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <SortableTh label="Feature Key" sortKey="feature_key" sort={urlState.sort} order={urlState.order} onSort={onSort} firstOrder="asc" />
              <th className={thClass}>Description</th>
              <SortableTh label="Active" sortKey="is_active" sort={urlState.sort} order={urlState.order} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortedFeatures.length === 0 ? (
              <EmptyRow colSpan={3} text="No features found." />
            ) : (
              sortedFeatures.map((feature) => (
                <tr
                  key={feature.id}
                  className={`hover:bg-body-bg transition-colors ${hasChange(feature) ? 'bg-yellow-900/20' : ''}`}
                >
                  <td className={tdClass}>
                    <span className="px-3 py-1 text-sm font-medium bg-body-bg rounded">
                      {feature.feature_key}
                    </span>
                  </td>
                  <td className={`${tdClass} whitespace-normal`}>{feature.description}</td>
                  <td className={tdClass}>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={getEffectiveValue(feature)}
                        onChange={(e) => onFeatureChange(feature.feature_key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableViewport>
    </TableCard>
  );
};

export default FeaturesTab;
